import { MouseEvent } from "react";

import CheckIcon from "@mui/icons-material/Check";
import {
  Box,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";

import { useMenu } from "components/Menu/context";
import { MenuNodeType } from "components/Menu/model";
import { useAutoFocus } from "hooks/useAutoFocus";
import { useAvoTracking } from "hooks/useAvoTracking";
import {
  getLanguageFromCookie,
  getLanguageFromMenuText,
  Language,
  LANGUAGE_MAPPING,
  setLanguageCookie,
} from "utils/language";
import { isCv1Page } from "utils/page";
import {
  getUrlWithoutHash,
  isHomepageUrl,
  isLegacyUrl,
  updateCV1LanguageUrl,
  updateCV2LanguageUrl,
} from "utils/url";

import { sharedStyles } from "../shared.styles";

import { languageSelectionMenuStyles } from "./languageSelectionMenu.styles";

interface LanguageSelectionProps {
  analyticsTag?: string;
  mouseEventHandler: MouseEvent<HTMLButtonElement>;
  language: Language;
}

export const LanguageSelectionMenu = ({
  node,
}: {
  node: MenuNodeType.Container;
}) => {
  const { trackMenuClick } = useAvoTracking();
  const { languageNodes } = useMenu();
  const currentLanguage = getLanguageFromCookie();
  const htmlButtonRef = useAutoFocus<HTMLButtonElement>(true);

  if (!node.childNodes) return null;

  const handleLanguageSelection = ({
    analyticsTag,
    mouseEventHandler,
    language,
  }: LanguageSelectionProps) => {
    setLanguageCookie(language);

    let redirectUrl = getUrlWithoutHash(location.href);
    const pathname = location.pathname;
    if (isHomepageUrl(pathname, languageNodes)) {
      redirectUrl =
        languageNodes.find((node) =>
          node.titleKey.includes(
            LANGUAGE_MAPPING[language].menuLanguagePreferenceText
          )
        )?.href ?? redirectUrl;
    } else if (isCv1Page()) {
      redirectUrl = updateCV1LanguageUrl(language, location.href);
    } else if (isLegacyUrl(pathname)) {
      redirectUrl = updateCV2LanguageUrl(language, location.href);
    }

    trackMenuClick({
      containerLinkText: node.analyticsTag,
      name: analyticsTag,
      redirectLinkProps: {
        mouseEventHandler,
        redirectUrl,
      },
    });
  };

  return (
    <>
      <Box css={sharedStyles.headerContainer}>
        <Typography
          variant="h2"
          css={sharedStyles.headerText}
          data-testid="language-selection-menu-header"
        >
          {node.titleKey}
        </Typography>
      </Box>
      <Divider css={sharedStyles.headerDivider} />
      {node.childNodes.map((childNode, idx) => {
        const { analyticsTag, titleKey: childTitleKey } =
          childNode as MenuNodeType.Link;

        const language = getLanguageFromMenuText(childTitleKey);
        return (
          <MenuItem
            tabIndex={0}
            component="button"
            css={languageSelectionMenuStyles.menuItem}
            key={childTitleKey}
            onClick={(e) =>
              handleLanguageSelection({
                analyticsTag,
                mouseEventHandler: e,
                language,
              })
            }
            {...(idx === 0 && { ref: htmlButtonRef })}
          >
            <ListItemIcon>
              {currentLanguage === language && <CheckIcon />}
            </ListItemIcon>
            <ListItemText>{childTitleKey}</ListItemText>
          </MenuItem>
        );
      })}
    </>
  );
};
